import { useState, useEffect, useContext, useMemo } from 'react';

/* contexts */
import { Context } from '../../../context/UserContext';

/* tema customizado*/
import tableThemeCustom from '../../form/tableThemeCustom';
import { Stack, Button, useMediaQuery } from '@mui/material';

/* hooks */
import useFlashMessage from '../../../hooks/useFlashMessage';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { filterTabsByProfile, getInitialTabKey } from '../../../utils/profileTabs';
import { useTabChangeFetch } from '../../../hooks/useTabChangeFetch';

/* TABS PANEL */
import SystemTabs from '../../../ui/SystemTabs';
import CabecalhoTabPanel from './tabs/cabecalhoTabPanel';
import BensTabPanel from './tabs/bensTabPanel';
import EmbalagensTabPanel from './tabs/embalagensTabPanel';

import FiscalTabPanel from './tabs/fiscalTabPanel';

import IndustriaTabPanel from './tabs/industriaTabPanel';
import ModoProducaoTabPanel from './tabs/modoProducaoTabPanel';
import MateriaPrimaTabPanel from './tabs/materiaPrimaTabPanel';
import CaracteristicasTabPanel from './tabs/caracteristicaTabPanel';
import ImagensTabPanel from './tabs/imagensTabPanel';

import { useCabecalhoModule } from './modules/useCabecalhoModule';
import { useFiscalProdutoModule } from './modules/useFiscalProdutoModule';
import { useBensModule } from './modules/useBensModule';
import { useIndustriaModule } from './modules/useIndustriaModule';
import { useMateriaPrimaModule } from './modules/useMateriaPrimaModule';
import { useEmbalagensModule } from './modules/useEmbalagensModule';
import { useImagensModule } from './modules/useImagensModule';

/* util */
import { obterPermissoesUsuario, pickSelectedId } from '../../../utils/funcoes';

function Form({
    token,
    handleSubmit,
    formData,
    btnText,
    caminhoVoltar,
    //enums
    EnumBemTipo,
    EnumTipo,
    EnumOrigemCadastro,
    EnumPosicao,
    //options
    OptionsAlmoxarifados,
    OptionsCores,
    OptionsEmbalagem,
    OptionsEmbalagemProducao,
    OptionsEmpresa,
    OptionsUnidade,
    //constantes
    empresaId,
    empresaIds,
    empresaTabelaId,
    //parametro
    profile,
}) {
    // define se o equipamento é mobile ou desktop para renderizar o layout correto
    /* modo tema */
    const theme = tableThemeCustom();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    //situação para submeter o formulario e evitar vários clicks
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [preview, setPreview] = useState([]);
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const { parametro } = useContext(Context);

    const urlParams = new URLSearchParams(window.location.search);

    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const duplicidade = urlParams.get('duplicidade');

    const params = new URLSearchParams({
        ul: ul,
        li: li,
        tipo: tipo,
        //operacao: operacaoUUID,
        //uuid: uuid,
        btnText: urlParams.get('btnText'),
    });

    const cabecalhoModule = useCabecalhoModule({
        formData,
        empresaId,
        empresaTabelaId,
        isMobile,
        navigate,
        setFlashMessage,
        pickSelectedId, // se você já tem
        btnText,
    });

    const { dados, setDados } = cabecalhoModule.state; // se quiser usar o mesmo nome "dados"

    //set datalist
    const [produtoBens, setBem] = useState(formData?.produtoBens || {});

    useEffect(() => {
        duplicidade && setIsSubmitting(false);
    }, [duplicidade]);

    useEffect(() => {
        // Calcula o volume se altura, largura ou comprimento forem alterados

        const altura = parseFloat(dados.altura) || 0;
        const largura = parseFloat(dados.largura) || 0;
        const comprimento = parseFloat(dados.comprimento) || 0;

        // Calcula o volume em m³
        const volumeM3 = altura * largura * comprimento;

        setDados((prev) => ({ ...prev, volume: volumeM3 }));
    }, [dados.altura, dados.largura, dados.comprimento]);

    //data and fetching state
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    //table state
    const [columnFilterFns, setColumnFiltersMode] = useState(
        localStorage.columnFilterFns.length > 2 ? JSON.parse(localStorage.columnFilterFns) : {}
    );
    const [columnFilters, setColumnFilters] = useState(
        localStorage.columnFilters.length > 2 ? JSON.parse(localStorage.columnFilters) : []
    );
    const [globalFilter, setGlobalFilter] = useState(() => {
        const storedFilter = localStorage.getItem('globalFilter');

        if (storedFilter && storedFilter !== 'undefined' && storedFilter.length > 2) {
            try {
                return JSON.parse(storedFilter);
            } catch (e) {
                console.warn('Erro ao parsear globalFilter:', e);
                return ''; // valor padrão
            }
        }
        return ''; // valor padrão
    });

    const [sorting, setSorting] = useState(localStorage.sorting.length > 2 ? JSON.parse(localStorage.sorting) : []);
    const [pagination, setPagination] = useState({
        pageIndex: JSON.parse(localStorage.pagination).pageIndex || 0,
        pageSize: JSON.parse(localStorage.pagination).pageSize || 5,
    });
    //envia MRT para o mudule
    const [rowCount, setRowCount] = useState(0);

    const mrt = useMemo(
        () => ({
            // table state
            columnFilterFns,
            setColumnFiltersMode,
            columnFilters,
            setColumnFilters,
            globalFilter,
            setGlobalFilter,
            sorting,
            setSorting,
            pagination,
            setPagination,

            // fetch state for MRT UI
            isError,
            isLoading,
            isRefetching,
            setIsError,
            setIsLoading,
            setIsRefetching,

            // server row count
            rowCount,
            setRowCount,
        }),
        [columnFilterFns, columnFilters, globalFilter, sorting, pagination, isError, isLoading, isRefetching, rowCount]
    );

    const industriaModule = useIndustriaModule({
        formData: dados,
        dados,
        setDados,
        parametro,
        profile,
        setFlashMessage,
        pickSelectedId,
        OptionsEmbalagemProducao,
    });
    const materiaPrimaModule = useMateriaPrimaModule({
        dados,
        setDados,
        parametro,
        profile,
        setFlashMessage,
        pickSelectedId,
    });
    const embalagensModule = useEmbalagensModule({
        dados,
        setDados,
        parametro,
        profile,
        mrt,
        OptionsEmbalagem,
        OptionsEmpresa,
        setFlashMessage,
    });
    const imagensModule = useImagensModule({ dados, setDados });

    const bensModule = useBensModule({
        dados,
        setDados,
        empresaId,
        tipo,
        empresaTabelaId,
        setFlashMessage,
        navigate,
        urlParams,
        //options e enums
        OptionsCores,
        EnumBemTipo,
        EnumPosicao,
        // Repasse o MRT para que o refresh/impressão funcione com os filtros da tela
        pagination,
        columnFilters,
        columnFilterFns,
        globalFilter,
        sorting,
        mrt,
    });

    const fiscalModule = useFiscalProdutoModule({
        dados,
        setDados,
        empresaId,
        setFlashMessage,
    });

    const submit = async (e) => {
        e.preventDefault();

        if (!dados.empresaId) {
            dados.empresaId = empresaId;
        }

        //validações
        if (!dados.ncmId) {
            setFlashMessage('NCM é um campo obrigatório!', 'error');
            return;
        }

        if (isSubmitting) return; // evita clique duplo
        // setIsSubmitting(true);

        handleSubmit(dados);
    };

    useEffect(() => {
        if (!token) {
            return navigate('/Auth', { replace: true });
        }

        //funcao para setar localStorage
        localStorage.setItem(
            'pagination',
            JSON.stringify({
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
            })
        );
        localStorage.setItem('columnFilterFns', JSON.stringify(columnFilterFns));
        localStorage.setItem('columnFilters', JSON.stringify(columnFilters));
        localStorage.setItem('globalFilter', JSON.stringify(globalFilter));
        localStorage.setItem('sorting', JSON.stringify(sorting));

        //permissões do usuario
        obterPermissoesUsuario(localStorage.getItem('pessoaId'), empresaId, ul, li);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);
    useEffect(() => {
        // evita tentar buscar antes de existir produtoBensId (se online)
        bensModule?.actions?.grid?.refresh?.();
    }, [
        dados?.id,
        dados?.produtoBens?.id,
        mrt.columnFilters,
        mrt.globalFilter,
        mrt.sorting,
        mrt.pagination.pageIndex,
        mrt.pagination.pageSize,
    ]);
    //uis por grid
    const uiTabCabecalho = profile.uiTabs.cabecalho;
    const uiTabCaracteristicas = profile.uiTabs.caracteristicas;
    const uiTabIndustria = profile?.uiTabs.industria;
    const uiTabMateriaPrima = profile?.uiTabs.materiaPrima;
    const uiTabModoProducao = profile?.uiTabs.modoProducao;
    const uiTabEmbalagem = profile.uiTabs.embalagem;
    const uiTabBens = profile?.uiTabs?.bens || {};

    const tabsConfig = useMemo(() => {
        return [
            {
                key: 'cabecalho',
                label: 'Cabeçalho',
                title: uiTabCabecalho?.titles?.grid || 'Cabeçalho',
                panel: (
                    <CabecalhoTabPanel
                        module={cabecalhoModule}
                        //options e enums
                        OptionsEmpresa={OptionsEmpresa}
                        EnumTipo={EnumTipo}
                        EnumOrigemCadastro={EnumOrigemCadastro}
                        profile={profile}
                    />
                ),
            },
            {
                key: 'fiscal',
                label: 'Fiscal',
                title: 'Configuração fiscal do produto',
                panel: <FiscalTabPanel module={fiscalModule} dados={dados} />,
            },
            {
                key: 'bens',
                label: uiTabBens?.labels?.grid,
                title: uiTabBens?.titles?.grid || 'Gestão de Ativos e Inventário',
                // A aba só aparece se o produto for marcado como imovel, vtr ou bem na Model de Produto
                show: !!(dados.imovel || dados.vtr || dados.bem),
                panel: (
                    <BensTabPanel
                        module={bensModule}
                        dados={dados}
                        setFlashMessage={setFlashMessage}
                        profile={profile}
                        theme={theme}
                        mrt={mrt} // Passa o objeto MRT para a Grid interna
                    />
                ),
            },
            {
                key: 'industria',
                label: 'Indústria',
                title: 'Industrialização do Produto',
                // Só renderiza se for indústria nos parâmetros do sistema
                show: parametro?.Empresa?.isIndustria === true,
                panel: (
                    <IndustriaTabPanel
                        module={industriaModule}
                        theme={theme}
                        profile={profile}
                        mrt={mrt}
                        isError={isError}
                        isLoading={isLoading}
                        isRefetching={isRefetching}
                    />
                ),
            },
            {
                key: 'materiaPrima',
                label: 'Matéria-prima',
                title: uiTabMateriaPrima?.titles?.grid || 'Composição do produto',
                // Só renderiza se for indústria nos parâmetros do sistema
                show: parametro?.Empresa?.isIndustria === true,
                panel: (
                    <MateriaPrimaTabPanel
                        module={materiaPrimaModule}
                        theme={theme}
                        mrt={mrt}
                        isError={isError}
                        isLoading={isLoading}
                        isRefetching={isRefetching}
                    />
                ),
            },
            {
                key: 'modoProducao',
                label: 'Modo de Produção',
                // Só renderiza se for indústria nos parâmetros do sistema
                show: parametro?.Empresa?.isIndustria === true,
                title: uiTabModoProducao?.titles?.grid || 'Modo de Produção',
                panel: <ModoProducaoTabPanel theme={theme} module={cabecalhoModule} profile={profile} />,
            },
            {
                key: 'embalagem',
                label: 'Embalagens',
                title: uiTabEmbalagem?.titles?.grid || 'Embalagens',
                panel: (
                    <EmbalagensTabPanel
                        theme={theme}
                        module={embalagensModule}
                        //parametros
                        profile={profile}
                        mrt={mrt}
                    />
                ),
            },
            {
                key: 'caracteristicas',
                label: 'Caracteristicas',
                title: uiTabCaracteristicas?.titles?.grid || 'Características',

                panel: (
                    <CaracteristicasTabPanel
                        theme={theme}
                        module={cabecalhoModule}
                        //parametros
                        profile={profile}
                        //options e enums
                        OptionsAlmoxarifados={OptionsAlmoxarifados}
                    />
                ),
            },
            {
                key: 'imagens',
                label: 'Imagens',
                title: 'Galeria de Fotos',
                panel: <ImagensTabPanel module={imagensModule} dados={dados} />,
            },
        ];
    }, [
        fiscalModule,
        dados,
        theme,
        //parametros
        profile,
        parametro,

        embalagensModule,
        //options e enums
        OptionsEmpresa,

        //uis
        uiTabCabecalho,
        uiTabCaracteristicas,
        uiTabEmbalagem,
        uiTabBens,
    ]);
    const tabsConfigFinal = useMemo(() => filterTabsByProfile(tabsConfig, profile, parametro), [tabsConfig, profile, parametro]);

    const initialTabKey = useMemo(() => getInitialTabKey(profile, { defaultKey: 'cabecalho' }), [profile]);
    const { onTabChange } = useTabChangeFetch({
        initialKey: initialTabKey,
        onChange: async (key) => {
            const paging = { start: 0, size: pagination.pageSize };

            const refreshActions = {
                industria: () => profile?.id !== 'bobina' && industriaModule.actions.refresh(paging),
                materiaPrima: () => materiaPrimaModule.actions.refresh(paging),
                embalagem: () => embalagensModule.actions.refresh({ ...paging, produtoId: dados.id }),
                bens: () => bensModule.actions.grid.refresh({ ...paging, produtoId: dados.id }),
            };

            if (refreshActions[key]) await refreshActions[key]();
        },
    });

    return (
        <form onSubmit={submit}>
            <h1>{profile?.labelForm}</h1>
            <SystemTabs tabs={tabsConfigFinal} onTabChange={onTabChange} initialKey={initialTabKey} />

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '1rem',
                    flexWrap: 'wrap', // Permite que os itens quebrem a linha quando necessário
                    justifyContent: 'space-between', // Distribui os itens igualmente
                }}
            >
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate(caminhoVoltar, params)}
                        style={{
                            marginLeft: '10px',
                            minWidth: '120px',
                            fontSize: '14px',
                            '@media (max-width: 600px)': {
                                fontSize: '12px', // Ajusta o tamanho da fonte em telas menores
                                padding: '8px 12px', // Ajusta o padding para manter o botão visível
                            },
                        }}
                    >
                        Voltar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="success"
                        style={{
                            marginLeft: '10px',
                            minWidth: '120px',
                            fontSize: '14px',
                            '@media (max-width: 600px)': {
                                fontSize: '12px', // Ajusta o tamanho da fonte em telas menores
                                padding: '8px 12px', // Ajusta o padding para manter o botão visível
                            },
                        }}
                        disabled={embalagensModule?.state?.isDisabled || isSubmitting}
                    >
                        {btnText}
                    </Button>
                </Stack>
            </div>
        </form>
    );
}

export default Form;
